import React, { useEffect, useState } from "react";
  import { BackwardOutlined } from "@ant-design/icons";
import { MapContainer, TileLayer, GeoJSON, Marker, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";

import L, { Icon } from 'leaflet';
import { Col, Row, Table, Button } from "antd";

import "leaflet/dist/leaflet.css";
import { util } from "../../utils/functions/custom";
import { recordError } from "src/utils/logService";
import ecData from "../../data/maps/eastern-cape-districts.json";
import fsData from "../../data/maps/free-state-districts.json";
import gpData from "../../data/maps/gauteng-districts.json";
import kzData from "../../data/maps/kzn-districts.json";
import lpData from "../../data/maps/limpopo-districts.json";
import mpData from "../../data/maps/mpumalanga-districts.json";
import ncData from "../../data/maps/northern-cape-districts.json";
import nwData from "../../data/maps/north-west-districts.json";
import wcData from "../../data/maps/western-cape-districts.json";
import mapData from "../../data/maps/south-africa-provinces.json";
import "../../styles/map.css";

import services from "../../services/eventsAnalyticsService";

const EventsMap = ({
  data, // This prop now correctly triggers the initial data load
  provinces,
  reportingYear,
  hoverText,
  dataSource,
  width = "95%",
  annotatedCssContainer = "census-info-hover",
  annotatedCssText = "census-info",
}) => {
  // Centre of South Africa
  const center = [-29.4856, 26.124];
  const ecCenter = [-31.896692754373387, 26.871363695141365];
  const fsCenter = [-28.38544932088062, 26.8123608109806];
  const gpCenter = [-25.981525658418395, 28.132192670563324];
  const lpCenter = [-23.899142977474483, 29.443874688484982];
  const mpCenter = [-25.692442622055232, 30.247206855622114];
  const ncCenter = [-29.298490822889054, 21.32752846993595];
  const nwCenter = [-26.52374038088475, 25.90034988589018];
  const wcCenter = [-33.3940439174762, 19.923749081068152];
  const kzCenter = [-28.743515700185945, 30.472169157775596];

  const [onselect, setOnselect] = useState({});
  const [masterData, setMasterData] = useState([]);
  const [columnOneLabel, setColumnOneLabel] = useState("Province");
  const [featureName, setFeatureName] = useState("Province");
  const [featureLabel, setFeatureLabel] = useState("in all Provinces");
  // Initialize with an empty GeoJSON object to ensure GeoJSON component handles it
  const [mainMapData, setMainMapData] = useState(mapData);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [mapZoom, setMapZoom] = useState(6);
  const [mapCenter, setMapCenter] = useState(center);
  const [locationData, setLocationData] = useState([]);
  const [provinceData, setProvinceData] = useState([]);
  const [provinceId, setProvinceId] = useState(0);
  const [reportYear, setReportYear] = useState(util.getCurrentRepotYear());

  // Effect to load initial data when the 'data' prop changes (e.g., from an API call)
  useEffect(() => {
    loadInitialData();
  }, [data, reportingYear]); // Add reportingYear as a dependency as well

  const loadInitialData = async () => {
    try {
      // If no data is provided, clear the map and reset states
      if (!data || data.length === 0) {
        setMainMapData(mapData); // Clear GeoJSON features
        setEventsTotal(0);
        setLocationData([]); // Clear any markers
        setMasterData([]); // Clear table data
        setProvinceData([]); // Clear province-specific data
        setFeatureName("Province"); // Reset feature name
        setFeatureLabel("in all Provinces"); // Reset label
        setColumnOneLabel("Province"); // Reset table column label
        setMapCenter(center); // Reset map center
        setMapZoom(6); // Reset zoom
        console.log("No data provided, map features cleared.");
        return; // Exit the function early
      }

      // If data exists, proceed with processing
      const sortedData = [...data].sort((a, b) => a.type.localeCompare(b.type)); // Create a copy to sort
      setReportYear(reportingYear);
      setFeatureName("Province");
      setFeatureLabel("in all Provinces");
      setMapCenter(center);
      setMapZoom(6);
      setMasterData(sortedData);
      setProvinceData(sortedData);

      // Create a deep copy of the initial map data to avoid direct mutation
      const updatedMapData = JSON.parse(JSON.stringify(mapData));

      // Reset ALL NYSEvents to 0 first
      updatedMapData?.features.forEach((feature) => {
        feature.properties.NYSEvents = 0;
      });

      let totalEvents = 0;
      sortedData.forEach((province) => {
        const feature = updatedMapData.features.find((mdata) => province.type === mdata.properties.name);
        if (feature) {
          feature.properties.NYSEvents = province.value;
        }
        totalEvents += province.value;
      });

      setMainMapData(updatedMapData); // Update the map with processed data
      setEventsTotal(totalEvents);
      setLocationData([]); // Ensure markers are cleared when going back to provinces view
    } catch (error) {
      recordError(error);
      // In case of an error, also clear the map
      setMainMapData({ type: 'FeatureCollection', features: [] });
      setEventsTotal(0);
      setLocationData([]);
      setMasterData([]);
      setProvinceData([]);
    }
  };

  const resetHighlight = (e) => {
    setOnselect({});
    e.target.setStyle(style(e.target.feature));
  };

  const highlightFeature = (e) => {
    var layer = e.target;
    var eventsCount = 0;
    const { name } = e.target.feature.properties;
    let res;
    // Determine if it's province or district level data
    if (featureName === "Province") { // Use featureName state to distinguish
      res = provinceData.find((data) => data.type === name);
    } else { // Must be District
      res = masterData.find((data) => data.type === name); // Use masterData for district events
    }

    if (res !== undefined) eventsCount = res.value;

    setOnselect({
      name: name,
      value: eventsCount,
    });
    layer.setStyle(highlightStyle);
  };

  const highlightStyle = {
    weight: 1,
    color: "black",
    fillOpacity: 1,
  };

  const onEachFeature = (feature, layer) => {
    try {
      layer.on({
        mouseover: highlightFeature,
        mouseout: resetHighlight,
        click: onMap,
      });
      // Ensure NYSEvents property exists before using it in bindTooltip
      const events = feature.properties.NYSEvents || 0;
      layer.bindTooltip(`${feature.properties.name} - ${events} event(s)`);
    } catch (error) {
      recordError(error);
    }
  };

  const onMap = async (evt) => {
    try {
      var layer = evt.target;
      if (layer.feature.properties.level === "3") return; // Prevent drilling down further than districts

      let idx = 0;
      let provinceAbbreviation = layer.feature.properties.abbreviation;
      let province = provinces.find(province => province.code === provinceAbbreviation);

      // Handle click on a Province
      if (layer.feature.properties.level === "2") {
        setColumnOneLabel(layer.feature.properties.name + " Districts");
        setFeatureLabel(" in " + layer.feature.properties.name);
        if (province !== undefined) setProvinceId(province.id);

        const response = await services.getProvinceDistrictsEvents(reportYear, province.id);
        if (response && response.status === 200) {
          const { data: districtEventsData } = response; // Renamed for clarity

          setMasterData(districtEventsData); // Update table data to districts

          districtEventsData.forEach((ddata) => {
            idx += ddata.value;
          });
          setEventsTotal(idx);

          loadMapData(layer.feature.properties, districtEventsData); // Pass district data to loadMapData
          generateMarkers(layer.feature.properties.abbreviation);
        }
      } else {
        // This case should ideally not be reached if level 3 is guarded,
        // but included for completeness if there's a different hierarchy.
        setFeatureLabel("all Provinces");
      }
    } catch (error) {
      recordError(error);
    }
  }

  const loadMapData = (clickedFeatureProperties, mastData) => {
    try {
      let tempData = { type: 'FeatureCollection', features: [] }; // Initialize with empty GeoJSON
      let zoomLevel = 7;
      let centerCoords = center;

      switch (clickedFeatureProperties.abbreviation) {
        case "EC":
          tempData = ecData;
          zoomLevel = 7;
          centerCoords = ecCenter;
          break;
        case "FS":
          tempData = fsData;
          centerCoords = fsCenter;
          zoomLevel = 7;
          break;
        case "KZN":
          tempData = kzData;
          centerCoords = kzCenter;
          zoomLevel = 7;
          break;
        case "GP":
          tempData = gpData;
          centerCoords = gpCenter;
          zoomLevel = 9;
          break;
        case "LP":
          tempData = lpData;
          centerCoords = lpCenter;
          zoomLevel = 8;
          break;
        case "MP":
          tempData = mpData;
          centerCoords = mpCenter;
          zoomLevel = 8;
          break;
        case "NC":
          tempData = ncData;
          centerCoords = ncCenter;
          zoomLevel = 7;
          break;
        case "NW":
          tempData = nwData;
          centerCoords = nwCenter;
          zoomLevel = 8;
          break;
        case "WC":
          tempData = wcData;
          centerCoords = wcCenter;
          zoomLevel = 7;
          break;
        default:
          tempData = { type: 'FeatureCollection', features: [] }; // Fallback to empty if not found
          break;
      }

      setMapZoom(zoomLevel);
      setMapCenter(centerCoords);

      let idx = 0;
      // Ensure tempData is not null or undefined before iterating
      if (tempData && tempData.features) {
        mastData.forEach((district) => {
          const feature = tempData.features.find((mdata) => mdata.properties.name.toLowerCase().includes(district.type.toLowerCase()));
          if (feature) {
            feature.properties.NYSEvents = district.value;
            idx += district.value;
          }
        });
      }

      setMainMapData(tempData); // Update map with district data
      setEventsTotal(idx);
      setLocationData([]); // Clear location markers until specific event locations are fetched for this view
      setFeatureName("District"); // Indicate that the map is now showing districts
    } catch (error) {
      recordError(error);
      // Clear map data on error
      setMainMapData({ type: 'FeatureCollection', features: [] });
      setEventsTotal(0);
      setLocationData([]);
    }
  }

  const generateMarkers = async (code) => {
    try {
      const prov = provinces.find(record => record.code === code);
      if (!prov) {
        setLocationData([]); // Clear markers if province not found
        return;
      }
      const response = await services.getEventLocations(reportYear, prov.id);
      if (response && response.status === 200) {
        const { data } = response;
        setLocationData(data);
      } else {
        setLocationData([]); // Clear markers if API call fails or no data
      }
    } catch (error) {
      recordError(error);
      setLocationData([]); // Clear markers on error
    }
  }

  const columns = [
    {
      title: columnOneLabel,
      dataIndex: "type",
      key: "type"
    },
    {
      title: "Number of events",
      dataIndex: "value",
      key: "value",
    }
  ];

  const mapPolygonColorToEvents = (numberOfEvents) => {
    if (featureName === "Province") {
      return numberOfEvents > 40 ? "#45B39D" : numberOfEvents > 20
        ? "#A9DFBF" : numberOfEvents > 10
          ? "#F9E79F" : numberOfEvents > 5
            ? "#F5B7B1" : numberOfEvents > 2
              ? "#F1948A" : numberOfEvents > 1
                ? "#F1948A" : numberOfEvents === 1
                  ? "#f7dfdc" : "#f8ffb8";
    } else { // For districts
      return numberOfEvents > 20 ? "#45B39D" : numberOfEvents > 10
        ? "#A9DFBF" : numberOfEvents > 8
          ? "#F9E79F" : numberOfEvents > 5
            ? "#F5B7B1" : numberOfEvents > 3
              ? "#F1948A" : numberOfEvents > 1
                ? "#F1948A" : numberOfEvents === 1
                  ? "#f7dfdc" : "#f8ffb8";
    }
  };

  const style = (feature) => {
    return {
      fillColor: mapPolygonColorToEvents(feature.properties.NYSEvents || 0), // Default to 0 if NYSEvents is undefined
      weight: 1.5,
      opacity: 1,
      color: "white",
      dashArray: "2",
      fillOpacity: 0.75,
    };
  };

  const mapStyle = {
    height: "600px",
    width: width,
    margin: "0 auto",
    zIndex: 1,
  };

  return (
    <div className="province-container">
      <Row>
        <Col span={16}>
          <div className="reportYear">
            <div
              style={{
                margin: "0px auto",
                fontSize: "16px",
                fontWeight: "bold",
                textAlign: "center"
              }}
            >
              Total number of events {featureLabel} - {eventsTotal}
            </div>
          </div>
        </Col>
      </Row>
      <br></br>
      <Row>
        <Col xs={18}>
          <MapContainer
            zoom={mapZoom}
            scrollWheelZoom={false}
            style={mapStyle}
            center={mapCenter}
            preferCanvas
            key={JSON.stringify(mapCenter) + mapZoom} // Key on center and zoom to force re-render if needed
          >
            <TileLayer url="http://{s}.tile.osm.org/{z}/{x}/{y}.png" />
            <GeoJSON
              key={JSON.stringify(mainMapData)} // Key on the GeoJSON data to force re-render when data changes (including clearing)
              data={mainMapData.features} // Pass only the features array
              style={style}
              onEachFeature={onEachFeature}
            />
            {featureName === "District" && locationData.length > 0 && ( // Only show markers if in District view and locationData exists
              <MarkerClusterGroup
                chunkedLoading
              > {
                  locationData.map((p, index) => ( // Added index for key prop
                    <Marker
                      key={`marker-${index}-${p.name}`} // Unique key for markers
                      position={p.latlong}
                      icon={
                        new Icon({
                          iconUrl:
                            'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
                          iconSize: [25, 41],
                          iconAnchor: [12, 41],
                        })
                      }
                    >
                      <Popup>
                        <>{"Event name : " + p.name} <br /></>
                        <>{"Venue      : " + p.venue} <br /></>
                        <>{"Date       : " + p.eventDate} <br /></>
                        <>{"Location : " + p.location} <br /></>
                        <>{"Host organisation : " + p.host} <br /></>
                      </Popup>
                    </Marker>
                  ))
                }
              </MarkerClusterGroup>
            )}
          </MapContainer>
        </Col>
        <Col xs={6}>
          <Row>
            <Col xs={24} md={24}>
              <Table
                dataSource={masterData}
                columns={columns}
                bordered
                size="middle"
                key="21"
                pagination={false} // Added pagination prop as Tables often benefit from this
                // Added a check for masterData length to apply scroll, useful for large tables
                scroll={masterData.length > 10 ? { y: 400 } : undefined}
              />
            </Col>
          </Row>
          {featureName !== "Province" && (
            <Row>
              <Col>
                <Button
                  className="right-align-button add-button"
                  icon={<BackwardOutlined />}
                  onClick={() => loadInitialData()} // Call loadInitialData to go back to initial province view
                >
                  Back
                </Button>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default EventsMap;
